import { useState, useEffect, useCallback, useRef } from 'react';
import { createPublicClient, http, formatUnits } from 'viem';
import { monad } from '../config/chains';
import { CA_ADDRESS } from '../config/constants';

// Standalone viem client (no wallet needed)
const client = createPublicClient({
    chain: monad,
    transport: http('https://rpc.monad.xyz'),
});

export interface OnChainStats {
    holders: number;
    totalTransfers: number;
    totalSupply: string;
    isLoading: boolean;
    error: string | null;
    lastUpdated: number | null;
    volume24h: number;
    buys24h: number;
    sells24h: number;
    liquidity: number;
    marketCap: number;
    fdv: number;
    priceUsd: number;
}

const DEFAULT_STATS: OnChainStats = {
    holders: 0,
    totalTransfers: 0,
    totalSupply: '0',
    isLoading: true,
    error: null,
    lastUpdated: null,
    volume24h: 0,
    buys24h: 0,
    sells24h: 0,
    liquidity: 0,
    marketCap: 0,
    fdv: 0,
    priceUsd: 0,
};

// Fetch trading stats from DexScreener API (reliable, no CORS issues)
async function fetchDexScreenerStats(address: string) {
    const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${address}`);
    if (!res.ok) throw new Error('DexScreener API failed');
    const data = await res.json();

    if (!data.pairs || data.pairs.length === 0) return null;

    // Pick the pair with the highest liquidity
    const pair = data.pairs
        .filter((p: any) => p.chainId === 'monad')
        .sort((a: any, b: any) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0))[0];

    if (!pair) return null;

    return {
        priceUsd: parseFloat(pair.priceUsd || '0'),
        volume24h: pair.volume?.h24 || 0,
        buys24h: pair.txns?.h24?.buys || 0,
        sells24h: pair.txns?.h24?.sells || 0,
        liquidity: pair.liquidity?.usd || 0,
        marketCap: pair.marketCap || 0,
        fdv: pair.fdv || 0,
    };
}

// Try fetching holder count from MonadScan etherscan-compatible API
async function fetchHolderCount(address: string): Promise<number | null> {
    const endpoints = [
        `https://api.socialscan.io/monad/v2/api?module=token&action=tokeninfo&contractaddress=${address}`,
        `https://monadscan.com/api?module=token&action=tokeninfo&contractaddress=${address}`,
    ];

    for (const url of endpoints) {
        try {
            const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
            if (!res.ok) continue;
            const data = await res.json();
            if (data.result && typeof data.result === 'object') {
                const holders = parseInt(data.result.holdersCount || data.result.holders || '0', 10);
                if (holders > 0) return holders;
            }
            if (Array.isArray(data.result) && data.result.length > 0) {
                const holders = parseInt(data.result[0].holdersCount || data.result[0].holders || '0', 10);
                if (holders > 0) return holders;
            }
        } catch {
            // Try next endpoint
        }
    }

    return null;
}

// Fetch total supply from contract (lightweight single RPC call)
async function fetchTotalSupply(tokenAddress: string): Promise<string> {
    try {
        const supply = await client.readContract({
            address: tokenAddress as `0x${string}`,
            abi: [{
                inputs: [],
                name: 'totalSupply',
                outputs: [{ name: '', type: 'uint256' }],
                stateMutability: 'view',
                type: 'function',
            }],
            functionName: 'totalSupply',
        });
        return formatUnits(supply as bigint, 18);
    } catch {
        return '0';
    }
}

// Fetch holder + transfer count from block explorer
async function fetchFromExplorerPage(address: string): Promise<{ holders: number; transfers: number } | null> {
    try {
        const res = await fetch(`https://monadvision.com/api/v2/tokens/${address}`, {
            signal: AbortSignal.timeout(5000),
        });
        if (res.ok) {
            const data = await res.json();
            return {
                holders: parseInt(data.holders || data.holders_count || '0', 10),
                transfers: parseInt(data.transfers_count || data.transfer_count || '0', 10),
            };
        }
    } catch {
        // Not available
    }

    try {
        const res = await fetch(`https://monadvision.com/api/v2/tokens/${address}/counters`, {
            signal: AbortSignal.timeout(5000),
        });
        if (res.ok) {
            const data = await res.json();
            return {
                holders: parseInt(data.token_holders_count || '0', 10),
                transfers: parseInt(data.transfers_count || '0', 10),
            };
        }
    } catch {
        // Not available
    }

    return null;
}

/**
 * Fetch on-chain stats for any token address.
 * @param tokenAddress - Token contract address (defaults to DUCK CA_ADDRESS)
 * @param refreshInterval - Refresh interval in ms (default 60000)
 */
export function useOnChainStats(tokenAddress?: string, refreshInterval = 900_000): OnChainStats {
    const address = tokenAddress || CA_ADDRESS;
    const [stats, setStats] = useState<OnChainStats>(DEFAULT_STATS);
    const addressRef = useRef(address);

    // Reset when token address changes
    useEffect(() => {
        if (addressRef.current !== address) {
            addressRef.current = address;
            setStats({ ...DEFAULT_STATS, isLoading: true });
        }
    }, [address]);

    const fetchStats = useCallback(async () => {
        const currentAddress = addressRef.current;
        try {
            const [dexData, holderCount, explorerData, totalSupply] = await Promise.all([
                fetchDexScreenerStats(currentAddress).catch(() => null),
                fetchHolderCount(currentAddress).catch(() => null),
                fetchFromExplorerPage(currentAddress).catch(() => null),
                fetchTotalSupply(currentAddress),
            ]);

            // Don't update if address changed during fetch
            if (addressRef.current !== currentAddress) return;

            let holders = 0;
            let totalTransfers = 0;

            if (explorerData && explorerData.holders > 0) {
                holders = explorerData.holders;
                totalTransfers = explorerData.transfers;
            }

            if (holders === 0 && holderCount && holderCount > 0) {
                holders = holderCount;
            }

            if (totalTransfers === 0 && dexData) {
                totalTransfers = (dexData.buys24h || 0) + (dexData.sells24h || 0);
            }

            setStats({
                holders,
                totalTransfers,
                totalSupply,
                isLoading: false,
                error: null,
                lastUpdated: Date.now(),
                volume24h: dexData?.volume24h || 0,
                buys24h: dexData?.buys24h || 0,
                sells24h: dexData?.sells24h || 0,
                liquidity: dexData?.liquidity || 0,
                marketCap: dexData?.marketCap || 0,
                fdv: dexData?.fdv || 0,
                priceUsd: dexData?.priceUsd || 0,
            });
        } catch (err) {
            if (addressRef.current !== currentAddress) return;
            console.error('[useOnChainStats] Failed:', err);
            setStats(prev => ({
                ...prev,
                isLoading: false,
                error: err instanceof Error ? err.message : 'Failed to fetch data',
            }));
        }
    }, [address]);

    useEffect(() => {
        fetchStats();
        const interval = setInterval(fetchStats, refreshInterval);
        return () => clearInterval(interval);
    }, [fetchStats, refreshInterval]);

    return stats;
}
